import { PaymentMethodDefinitionIdentifier, RestApiPaymentMethodDto } from '@order-mage/integration-sdk';
import { Country } from '@/shared/model/Country';
import { Currency } from '@/shared/model/Currency';

export type PaymentType = 'cash' | 'card';

export const getPaymentType = (paymentMethod: RestApiPaymentMethodDto | undefined): PaymentType => {
    if (paymentMethod?.definition?.identifier === PaymentMethodDefinitionIdentifier.CASH) {
        return 'cash';
    }
    return 'card';
};

/**
 * Rounding rules:
 *
 * Card / online payment — always 2 decimal places, no cash rounding.
 *
 * Cash:
 *   CZK            → round to whole korunas (0 decimals)
 *   EUR (Slovakia) → round to nearest 0.05
 *   EUR (other)    → 2 decimal places
 *   GBP            → 2 decimal places
 */
export const roundPrice = (
    amount: number,
    currency: Currency,
    country: Country,
    paymentType: PaymentType,
): number => {
    if (paymentType === 'card') {
        return Math.round(amount * 100) / 100;
    }

    // Cash rounding
    if (currency === Currency.CZK) {
        return Math.round(amount);
    }

    if (currency === Currency.EUR && country === Country.SK) {
        // Round to nearest 5 cents
        return Math.round(amount * 20) / 20;
    }

    // EUR (non-SK) and GBP — 2 decimal places
    return Math.round(amount * 100) / 100;
};

/**
 * Tips are ALWAYS rounded by the country's cash rule — CZK → whole korunas,
 * EUR/SK → nearest 0.05, EUR (other) & GBP → 2 decimals — regardless of the
 * selected payment method, because a fractional tip breaks the POS / cash
 * register (OMP-2166, comment 2026-06-22). Unlike the grand total, card payments
 * are NOT exempt here.
 */
export const roundTip = (amount: number, currency: Currency, country: Country): number =>
    roundPrice(amount, currency, country, 'cash');

export const roundPriceToString = (
    amount: number,
    currency: Currency,
    country: Country,
    paymentType: PaymentType,
): string => {
    const rounded = roundPrice(amount, currency, country, paymentType);

    if (paymentType === 'card') {
        return rounded.toFixed(2);
    }

    if (currency === Currency.CZK) {
        return rounded.toFixed(0);
    }

    return rounded.toFixed(2);
};
